/**
 * The payments matrix's "Show forecasted values" checkbox.
 *
 * Two requirements meet here: the default *follows the window* (a window that
 * ends in the current month or later is exactly the case where a forecast says
 * something), and an explicit choice is remembered like the other view prefs.
 * So the stored value only exists once the user has actually clicked — until
 * then the checkbox tracks the window instead of freezing on it.
 *
 * View state, not a filter: it lives out of the URL on purpose (same reasoning
 * as `use-persisted-flag.ts`), so a shared link never carries an opinion about
 * how somebody else's screen was set.
 */

import { useCallback, useEffect, useState } from 'react';

export const FORECAST_PREF_KEY = 'payments.forecastVisible';

/** `null` = no stored choice yet — the window decides. */
function readStoredChoice(): boolean | null {
  try {
    const raw = window.localStorage.getItem(FORECAST_PREF_KEY);
    if (raw === 'true') return true;
    if (raw === 'false') return false;
    return null;
  } catch {
    return null;
  }
}

export function useForecastToggle(defaultFromWindow: boolean): [boolean, (next: boolean) => void] {
  // `null` is a real state, not a missing one: it means "follow the window".
  const [choice, setChoice] = useState<boolean | null>(() => readStoredChoice());

  useEffect(() => {
    if (choice === null) return;
    try {
      window.localStorage.setItem(FORECAST_PREF_KEY, String(choice));
    } catch {
      // Unpersistable is not unusable — keep the in-memory state.
    }
  }, [choice]);

  const setValue = useCallback((next: boolean) => setChoice(next), []);
  return [choice ?? defaultFromWindow, setValue];
}
